<template>
  <form ref="form" @submit.prevent="handleSubmit">
    <v-container class="main-container responsive-height">
      <v-row>
        <v-col xl="6" lg="6" md="6" sm="12" cols="12">
          <v-card class="deposit-form" :color="$vuetify.theme.dark ? '#131c29' : '#E0EAF2'">
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <fieldset>
                  <legend>Transfer Method</legend>
                  <v-text-field label="Touch n Go Deposit" disabled placeholder="Touch n Go Transfer" single-line solo
                    hide-details readonly class="custom-select" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <fieldset>
                  <legend>Deposit to</legend>
                  <v-select v-model="selectedAccount" :items="formattedAccounts" item-text="display"
                    item-value="trading_account_no" solo append-icon="mdi-chevron-down" label="Select One"
                    class="custom-select" hide-details @change="handleAccountChange" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="8">
                <fieldset>
                  <legend>
                    Amount in (USD)
                    <small class="text-muted" v-if="minTransaction">
                      (Min: {{ minTransaction }})
                    </small>
                  </legend>

                  <v-text-field v-model="depositAmount" label="$" type="number" single-line solo hide-details
                    class="custom-select" :min="minTransaction" :rules="[rules.required, minAmountRule]" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="4">
                <fieldset>
                  <legend>
                    Amount in <span :style="{ color: currency.color }">({{ currency.name }})</span>
                  </legend>
                  <v-text-field readonly v-model="currencyAmount" :label="currency.symbol" type="number" single-line
                    solo hide-details class="custom-select text-muted" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="12" v-if="needSlip == 1">
                <div class="input-file">
                  <span>Upload Pay.Proof</span>
                  <v-file-input v-model="bankSlip" label="Choose file" filled prepend-icon="mdi-file-image"
                    hide-details />
                </div>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <v-btn type="submit" tile elevation="0" class="ma-0 text-capitalize btn-account d-block"
                  color="greenMain" :loading="loading" :disabled="!isFormValid || loading">
                  Deposit
                </v-btn>
              </v-col>

              <v-col cols="12" sm="12" md="12" class="crypto-div">
                <v-card-title class="title px-0 py-0">Instruction:</v-card-title>
                <ol class="instruction">
                  <li>
                    Payments from third parties are not accepted. The name of the sender must match the account holder's
                    name.
                  </li>
                  <li>
                    Broker accepts wire transfers in USD. Funds sent in a different currency will be converted at the
                    current exchange rate.
                  </li>
                </ol>
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <v-col xl="6" lg="6" md="6" sm="12" cols="12">
          <v-card class="elevation-0 rounded-0 py-4 mt-2" :color="$vuetify.theme.dark ? '#162334' : '#F1F7FB'">
            <v-card-text class="m-0 p-0">
              <h3 class="text-center m-0 py-2">
                AC:<b style="color: #039DE9;"> 9999001087822</b>
              </h3>
            </v-card-text>

            <div class="qr-image">
              <img :src="qrCodeUrl" class="rounded" alt="QR Code" width="400" />
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </form>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { DEPOSITMETHODS__ACTIONS } from "@/store/action-types";
import toast from "@/services/Notification";

export default {
  name: "TouchNGoDepositFrom",

  data() {
    return {
      payment_id: null,
      selectedAccount: null,
      depositAmount: "",
      currencyAmount: "",
      bankSlip: null,
      loading: false,

      rules: {
        required: (value) => !!value || "This field is required",
      },
    };
  },

  computed: {
    ...mapState("deposit", ["accounts", "needSlip", "paymentSystem", "currency"]),

    formattedAccounts() {
      return (this.accounts || []).map((account) => ({
        display: `${account.trading_account_no} (${account.account_type_display}) ${account.Balance} ${account.currency_name}`,
        trading_account_no: account.trading_account_no,
        ...account,
      }));
    },

    selectedAccountDetails() {
      return (this.accounts || []).find((acc) => acc.trading_account_no === this.selectedAccount);
    },

    qrCodeUrl() {
      return this.paymentSystem?.qr_file || "";
    },

    //  DB/API min_transaction deposit set
    minTransaction() {
      const v = Number(this.paymentSystem?.min_transaction);
      return Number.isFinite(v) && v > 0 ? v : 10; // fallback 10
    },

    //  dynamic rule (minTransaction)
    minAmountRule() {
      return (value) => {
        const amt = Number(value);
        if (!amt) return "This field is required";
        return amt >= this.minTransaction || `Amount must be at least ${this.minTransaction}`;
      };
    },

    isFormValid() {
      const amountOk = Number(this.depositAmount) >= this.minTransaction;
      const slipOk = this.needSlip == 1 ? !!this.bankSlip : true;

      return !!this.selectedAccount && amountOk && slipOk;
    },
  },

  watch: {
    depositAmount(newAmount) {
      const amt = Number(newAmount);
      if (this.currency && this.currency.rate) {
        this.currencyAmount = (amt * Number(this.currency.rate)).toFixed(2);
      } else {
        this.currencyAmount = newAmount;
      }
    },
  },

  methods: {
    ...mapActions("deposit", [
      DEPOSITMETHODS__ACTIONS.DEPOSITACCOUNTS,
      DEPOSITMETHODS__ACTIONS.CREATEGPAYDEPOSIT,
      DEPOSITMETHODS__ACTIONS.GETPAYMENTSYSTEM,
      DEPOSITMETHODS__ACTIONS.NEEDSLIP,
      DEPOSITMETHODS__ACTIONS.GETCURRENCY,
    ]),

    handleAccountChange() {
      this.depositAmount = "";
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },

    validateForm() {
      if (!this.selectedAccount) {
        toast.error("Please select an account");
      }

      const amt = Number(this.depositAmount);

      if (!amt || amt <= 0) {
        toast.error("Please enter a valid deposit amount");
      }

      if (amt && amt < this.minTransaction) {
        toast.error(`Minimum deposit is ${this.minTransaction}`);
      }

      if (!this.bankSlip && this.needSlip == 1) {
        toast.error("Please upload payment proof");
      }
    },

    async handleSubmit() {
      try {
        this.validateForm();

        // extra guard 
        if (!this.isFormValid) return;

        this.loading = true;

        const formData = new FormData();
        formData.append("trading_account_no", this.selectedAccount);
        formData.append("amount", this.depositAmount);
        formData.append("payment_slip", this.bankSlip);
        formData.append("payment_id", this.payment_id);

        await this[DEPOSITMETHODS__ACTIONS.CREATEGPAYDEPOSIT](formData);

        this.resetForm();
      } catch (error) {
        console.error("Deposit submission error:", error);
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.$nextTick(() => {
        this.selectedAccount = null;
        this.depositAmount = "";
        this.bankSlip = null;

        if (this.$refs.form) {
          this.$refs.form.reset();
        }

        const fileInput = this.$refs.fileInput;
        if (fileInput) {
          fileInput.reset();
        }
      });
    },
  },

  async created() {
    //  payment_id set first, than API call
    this.payment_id = this.$route.query.payment_id;

    await this[DEPOSITMETHODS__ACTIONS.NEEDSLIP]();
    await this[DEPOSITMETHODS__ACTIONS.GETCURRENCY]({ payment_id: this.payment_id });
    await this[DEPOSITMETHODS__ACTIONS.GETPAYMENTSYSTEM]({ payment_id: this.payment_id });
  },

  async mounted() {
    try {
      await Promise.all([this[DEPOSITMETHODS__ACTIONS.DEPOSITACCOUNTS]()]);
    } catch (error) {
      console.error("Failed to fetch initial data:", error);
    }
  },
};
</script>

<style scoped>
.trade {
  margin-left: -15px;
  font-weight: 300;
  font-size: 13px;
}

.crypto-div {
  min-height: 320px;
}

.crypto-form-card {
  height: 420px;
}

.main {
  height: 100vh !important;
  overflow-y: scroll;
  margin: 0;
}

.qr-image {
  text-align: center;
}
</style>
